import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Row, Col, Form, Select, Input, Button, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'; // Import Reload icon
import bodyType from '../../helpers/consts/bodyType';
import fuelType from '../../helpers/consts/fuelType';
import colorType from '../../helpers/consts/colorType';
import bg from '../../assets/img/bg_02.jpg';
import { fetchMakes } from '../../store/actions/vehicleMakes-actions';
import { fetchModelsByMake } from '../../store/actions/vehicleModels-actions';

const { RangePicker } = DatePicker;
const { Option } = Select;

const VehiclesSearchBar = ({
  onSearch,
  onRangeDate,
  initialValues,
  userList = [],
  carHouses = [],
  onUserChange,
  setSelectedCarHouseId,
  role,
  isPurchaseVehicles,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [makes, setMakes] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);
  const dispatch = useDispatch();
  const [selectedCarHouse, setSelectedCarHouse] = useState(null);

  const translatedBodyType = bodyType.map((item) => ({
    value: item.value,
    label: t(`vehicles.bodyTypeOptions.${item.value}`) || item.label,
  }));

  const translatedColorType = colorType.map((item) => ({
    value: item.value,
    label: t(`vehicles.colorTypeOptions.${item.value}`) || item.label,
  }));

  const translatedFuelType = fuelType.map((item) => ({
    value: item.value,
    label: t(`vehicles.fuelTypeOptions.${item.value}`) || item.label,
  }));

  useEffect(() => {
    dispatch(fetchMakes())
      .then((data) => {
        setMakes(data.payload);
      })
      .catch((error) => {
        console.error('Error fetching makes:', error);
      });
  }, [dispatch]);

  useEffect(() => {
    if (selectedMake?.id) {
      dispatch(fetchModelsByMake({ vehicleMakeId: selectedMake.id }))
        .then((data) => {
          setFilteredModels(data.payload);
        })
        .catch((error) => {
          console.error('Error fetching models:', error);
        });
    }
  }, [dispatch, selectedMake]);

  useEffect(() => {
    // Set initial values for the form
    form.setFieldsValue(initialValues);

    // If there is a make in the initial values, set the selected make and fetch models
    if (initialValues?.make) {
      const selectedMakeObj = makes.find(
        (make) => make.name === initialValues.make
      );
      setSelectedMake(selectedMakeObj);
      if (selectedMakeObj) {
        dispatch(fetchModelsByMake({ vehicleMakeId: selectedMakeObj.id }))
          .then((data) => {
            setFilteredModels(data.payload);
          })
          .catch((error) => {
            console.error('Error fetching models:', error);
          });
      }
    }
  }, [initialValues, makes, form, dispatch]);

  const onSearchHandler = (value) => {
    if (selectedMake) {
      const searchData = {
        ...value,
        make: selectedMake.name,
      };
      onSearch(searchData);
    } else {
      onSearch(value);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setSelectedMake(null);
    setFilteredModels([]);
    onRangeDate(null, null);
    onSearch();
  };

  const handleMakeChange = (makeId) => {
    const selectedMakeObj = makes.find((make) => make.id === makeId);
    setSelectedMake(selectedMakeObj);
    form.setFieldsValue({
      model: null,
    });
  };

  const handleCarHouseChange = (carHouseId) => {
    const selectedCarHouseObj = carHouses.find(
      (carHouse) => carHouse.id === carHouseId
    );
    setSelectedCarHouse(selectedCarHouseObj);
    setSelectedCarHouseId(selectedCarHouseObj?.id);
    form.setFieldsValue({
      userId: null,
    });
  };

  return (
    <div
      className="vehicleSearchBarBg"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <Form
        className="container-fluid p-0 searchForm"
        form={form}
        layout="vertical"
        name="form"
        onFinish={onSearchHandler}
      >
        <Row gutter={16}>
          {/* Make */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="make" style={{ marginBottom: '10px' }}>
              <Select
                placeholder={t('form.vehicleForm.vehicleInformationField.makePlaceholder')}
                onChange={handleMakeChange}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {makes?.map((make) => (
                  <Option key={make.id} value={make.id}>
                    {make.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Model */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="model">
              <Select
                placeholder={t('form.vehicleForm.vehicleInformationField.modelPlaceholder')}
                showSearch
                allowClear
                disabled={!selectedMake}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filteredModels.map((model) => (
                  <Option key={model.id} value={model.name}>
                    {model.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Model Year */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="modelYear">
              <Input placeholder={t('form.vehicleForm.vehicleAdditionalDetails.modelYear')} allowClear />
            </Form.Item>
          </Col>

          {/* Color */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="color">
              <Select
                placeholder={t('vehicles.selectColor')}
                options={translatedColorType}
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Engine Size */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="engineSize">
              <Input placeholder={t('form.vehicleForm.vehicleAdditionalDetails.engineSize')} allowClear suffix={'cm3'} />
            </Form.Item>
          </Col>

          {/* Body Type */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="bodyType">
              <Select
                placeholder={t('vehicles.selectBodyType')}
                options={translatedBodyType}
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Fuel Type */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="fuelType">
              <Select
                placeholder={t('vehicles.selectFuelType')}
                options={translatedFuelType}
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Horse Power */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <Form.Item name="horsePower">
              <Input placeholder={t('form.vehicleForm.vehicleAdditionalDetails.horsePower')}  allowClear suffix={'hp'} />
            </Form.Item>
          </Col>

          {/* Vin */}
          <Col xs={24} sm={12} md={8} lg={12} xl={8}>
            <Form.Item name="vin">
              <Input placeholder={t('form.vehicleForm.vehicleAdditionalDetails.vin')} allowClear />
            </Form.Item>
          </Col>

          {/* Date Range 
          <Col xs={24} sm={8}>
            <Form.Item name="dateRange">
              <RangePicker allowClear onChange={onRangeChange} style={{width: '100%'}}/>
            </Form.Item>
          </Col>*/}
          {/* NEW: User dropdown for client admin */}
          {role === 'carHouseAdmin' && isPurchaseVehicles && (
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <Form.Item name="userId">
                <Select
                  placeholder={t('vehicles.selectUser')}
                  onChange={onUserChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {userList.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>

        {role === 'root' && (
          <Row gutter={[16, 16]}>
            <Col>
              <h4>{t('vehicles.buyers')}: </h4>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <Form.Item name="carHouseId">
                <Select
                  placeholder={t('vehicles.selectCarHouse')}
                  onChange={handleCarHouseChange}
                  value={selectedCarHouse}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {carHouses.map((house) => (
                    <Option key={house.id} value={house.id}>
                      {house.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <Form.Item name="userId">
                <Select
                  placeholder={t('vehicles.selectUser')}
                  onChange={onUserChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  disabled={!selectedCarHouse}
                >
                  {userList.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Action Buttons */}
        <Row justify="end" gutter={[16, 16]}>
          <Col xs={24} sm={12} md={4}>
            <Button
              type="primary"
              shape="round"
              icon={<SearchOutlined />}
              htmlType="submit"
              style={{ width: '100%' }}
            >
              {t('common.search')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              type="default"
              shape="round"
              icon={<ReloadOutlined />}
              onClick={resetForm}
              style={{ width: '100%' }}
            >
              {t('common.reset')}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default VehiclesSearchBar;
